/**
 * Every page URL of the app, laid out as `/<menu group>/<page>` after the HR Module Navigation Map
 * (see .claude/plans/prototype-revamp.md). Link to a page through these, never with a literal string,
 * so a page that moves changes in one place. The old Angular-era `/hr/*` URLs redirect here
 * (`LEGACY_REDIRECTS` in src/routes/legacy-redirects.tsx), because notifications and e-mails already sent
 * still point to them.
 *
 * A few pages are temporary siblings until the phase that merges them into another page's tab
 * (MBM Projects → Projects, Performance Dashboard → All Reviews).
 */
export const APP_PATHS = {
  HOME: '/home',

  MY_PROFILE: '/my-profile',
  MY_PROFILE_EDIT: '/my-profile/edit',

  PEOPLE: '/people',
  PEOPLE_NEW: '/people/new',

  TIME_TRACKER: '/time-attendance/time-tracker',
  ATTENDANCE: '/time-attendance/attendance',
  TEAM_ATTENDANCE: '/time-attendance/team-attendance',
  TEAM_TIMESHEETS: '/time-attendance/team-timesheets',

  MY_TIME_OFF: '/time-off/my-time-off',
  TEAM_TIME_OFF: '/time-off/team-time-off',
  TIME_OFF_CALENDAR: '/time-off/calendar',
  SICK_LEAVE: '/time-off/sick-leave',

  MY_REVIEWS: '/performance/my-reviews',
  MY_REVIEW_SUBMISSIONS: '/performance/my-reviews/submissions',
  ALL_REVIEWS: '/performance/all-reviews',
  PERFORMANCE_DASHBOARD: '/performance/dashboard',

  CANDIDATES: '/hiring/candidates',
  CANDIDATES_NEW: '/hiring/candidates/new',
  OFFERS: '/hiring/offers',
  OFFERS_NEW: '/hiring/offers/new',

  ALL_SURVEYS: '/surveys/all-surveys',
  MY_SURVEY_RESPONSES: '/surveys/my-responses',

  ORDER_MEAL: '/meal/order-meal',
  MEAL_ORDERS: '/meal/meal-orders',

  SERVICE_REQUESTS: '/service-requests',

  SETTINGS: '/settings',
  DEPARTMENTS: '/settings/organization/departments',
  DESIGNATIONS: '/settings/organization/designations',
  LOCATIONS: '/settings/organization/locations',
  PROJECTS: '/settings/organization/projects',
  MBM_PROJECTS: '/settings/organization/mbm-projects',
  LEAVE_POLICIES: '/settings/time-off/leave-policies',
  HOLIDAYS: '/settings/time-off/holidays',
  REVIEW_TEMPLATES: '/settings/performance/review-templates',
  KPIS: '/settings/performance/kpis',
  SKILLS: '/settings/performance/skills',
  SURVEY_FORMS: '/settings/surveys/survey-forms',
  CTC_LEVELS: '/settings/compensation/ctc-levels',
  NOC_TYPES: '/settings/service-requests/noc-types',
  ROLES: '/settings/access-approvals/roles',
  AUDIT_LOG: '/settings/system/audit-log',

  // Settings pages that are not built yet: they show a "Soon" page (src/modules/settings).
  DOCUMENTS: '/settings/organization/documents',
  LEAVE_RULES: '/settings/time-off/leave-rules',
  WORK_SCHEDULES: '/settings/time-attendance/work-schedules',
  ATTENDANCE_RULES: '/settings/time-attendance/attendance-rules',
  TIMESHEET_RULES: '/settings/time-attendance/timesheet-rules',
  REVIEW_RULES: '/settings/performance/review-rules',
  REQUEST_TYPES: '/settings/service-requests/request-types',
  APPROVALS: '/settings/access-approvals/approvals',
  MEAL_MENU: '/settings/admin/meals/menu',
  MEAL_RULES: '/settings/admin/meals/rules',
} as const;

/** The service request types, by their URL segment under `/service-requests`. */
export const SERVICE_REQUEST_TYPES = {
  NOC: 'noc',
  BYOD: 'byod',
  BUSINESS_CARD: 'business-card',
  ID_CARD: 'id-card',
  INSURANCE: 'insurance',
} as const;

export type ServiceRequestType = (typeof SERVICE_REQUEST_TYPES)[keyof typeof SERVICE_REQUEST_TYPES];

/** The tabs of Settings › KPIs (`?tab=`). Weights was the KPI Configuration page until Phase 8. */
/** The tabs of My Reviews (`?tab=`): peer requests to answer, and review forms assigned to me. */
export const MY_REVIEWS_TABS = {
  REQUESTS: 'requests',
  SUBMISSIONS: 'submissions',
} as const;
export type MyReviewsTab = (typeof MY_REVIEWS_TABS)[keyof typeof MY_REVIEWS_TABS];

export const KPIS_TABS = {
  LIST: 'list',
  WEIGHTS: 'weights',
} as const;
export type KpisTab = (typeof KPIS_TABS)[keyof typeof KPIS_TABS];

/** The tabs of Settings › Projects (`?tab=`). MBM Projects was its own page until Phase 8. */
export const PROJECTS_TABS = {
  KOSOVO: 'kosovo',
  MBM: 'mbm',
} as const;
export type ProjectsTab = (typeof PROJECTS_TABS)[keyof typeof PROJECTS_TABS];

/** The tabs of Order Meal and Meal Orders (`?tab=`); Dinner is Soon, History is Order Meal's own. */
export const MEAL_TABS = {
  LUNCH: 'lunch',
  DINNER: 'dinner',
  HISTORY: 'history',
} as const;
export type MealTab = (typeof MEAL_TABS)[keyof typeof MEAL_TABS];

/** The tabs of People (`?tab=`): current employees, and alumni (deactivated logins). */
export const PEOPLE_TABS = {
  EMPLOYEES: 'employees',
  ALUMNI: 'alumni',
} as const;
export type PeopleTab = (typeof PEOPLE_TABS)[keyof typeof PEOPLE_TABS];

/** The tabs of Team Attendance (`?tab=`). Overtime was the Overtime Manager page until Phase 1. */
export const TEAM_ATTENDANCE_TABS = {
  EMPLOYEES: 'employees',
  CORRECTIONS: 'corrections',
  OVERTIME: 'overtime',
} as const;
export type TeamAttendanceTab = (typeof TEAM_ATTENDANCE_TABS)[keyof typeof TEAM_ATTENDANCE_TABS];

/** The tabs of My Time Off (`?tab=`). TOIL Requests was the My TOIL page until Phase 2. */
export const MY_TIME_OFF_TABS = {
  HISTORY: 'history',
  ALLOCATION: 'allocation',
  TOIL: 'toil',
} as const;
export type MyTimeOffTab = (typeof MY_TIME_OFF_TABS)[keyof typeof MY_TIME_OFF_TABS];

/** The tabs of Team Time Off (`?tab=`). TOIL Requests was the TOIL Manager page until Phase 2. */
export const TEAM_TIME_OFF_TABS = {
  OVERVIEW: 'overview',
  REQUESTS: 'requests',
  TOIL: 'toil',
} as const;
export type TeamTimeOffTab = (typeof TEAM_TIME_OFF_TABS)[keyof typeof TEAM_TIME_OFF_TABS];

/** The tabs of Service Requests (`?tab=`); each picks a request type (`?type=`). */
export const SERVICE_REQUEST_TABS = {
  MY: 'my',
  TEAM: 'team',
  ALL: 'all',
} as const;
export type ServiceRequestTab = (typeof SERVICE_REQUEST_TABS)[keyof typeof SERVICE_REQUEST_TABS];

/** The tabs of one employee's attendance page (`?tab=`). */
export const EMPLOYEE_ATTENDANCE_TABS = {
  LIST: 'list',
  CALENDAR: 'calendar',
  OVERTIME: 'overtime',
} as const;
export type EmployeeAttendanceTab =
  (typeof EMPLOYEE_ATTENDANCE_TABS)[keyof typeof EMPLOYEE_ATTENDANCE_TABS];

/** Pages of one record or one sub-page, built from an id. */
export const appPath = {
  person: (employeeId: string) => `${APP_PATHS.PEOPLE}/${employeeId}`,
  personEdit: (employeeId: string) => `${APP_PATHS.PEOPLE}/${employeeId}/edit`,
  /** One employee's attendance page; `tab` opens one of its tabs (`EMPLOYEE_ATTENDANCE_TABS`). */
  teamAttendanceOf: (userId: string, tab?: EmployeeAttendanceTab) =>
    `${APP_PATHS.TEAM_ATTENDANCE}/${userId}${tab ? `?tab=${tab}` : ''}`,
  teamTimesheetOf: (userId: string) => `${APP_PATHS.TEAM_TIMESHEETS}/${userId}`,
  reviewSubmission: (pmReviewDetailId: string) =>
    `${APP_PATHS.MY_REVIEW_SUBMISSIONS}/${pmReviewDetailId}`,
  review: (reviewId: string) => `${APP_PATHS.ALL_REVIEWS}/${reviewId}`,
  /** The performance dashboard of a review's employee, opened from the review's details. */
  reviewDashboard: (reviewId: string) => `${APP_PATHS.ALL_REVIEWS}/${reviewId}/dashboard`,
  survey: (batchId: string) => `${APP_PATHS.ALL_SURVEYS}/${batchId}`,
  surveyResponse: (batchId: string, submissionId: string) =>
    `${APP_PATHS.ALL_SURVEYS}/${batchId}/${submissionId}`,
  mySurveyResponse: (submissionId: string) => `${APP_PATHS.MY_SURVEY_RESPONSES}/${submissionId}`,
  /** The Service Requests page on one type's list (`?type=`), on `tab` when given. */
  serviceRequests: (type: ServiceRequestType, tab?: ServiceRequestTab) =>
    `${APP_PATHS.SERVICE_REQUESTS}?${tab ? `tab=${tab}&` : ''}type=${type}`,
  /** The apply form; with an id it edits that application. */
  serviceRequestForm: (type: ServiceRequestType, applicationId?: string) =>
    `${APP_PATHS.SERVICE_REQUESTS}/${type}/new${applicationId ? `?id=${applicationId}` : ''}`,
  nocReport: (applicationId: string) =>
    `${APP_PATHS.SERVICE_REQUESTS}/${SERVICE_REQUEST_TYPES.NOC}/${applicationId}/report`,
  reviewTemplate: (formId: string) => `${APP_PATHS.REVIEW_TEMPLATES}/${formId}`,
  surveyForm: (formId: string) => `${APP_PATHS.SURVEY_FORMS}/${formId}`,
} as const;
